import type { MonitorTripPlace } from '@/apis/monitor';
import type { MeasureSeries, SeriesBySensor } from '@/apis/telemetry';
import { haversineKm } from '../Home/map/MapUtils';
import { formatDurationMinutes } from '../Home/map/needsAttention';

/**
 * Pure numbers for the seatback-style trip panel. Times are ISO strings from
 * the trip endpoint; `now` is injected so the panel is testable.
 */

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  return Number.isFinite(ms) ? ms : null;
};

type Point = { lat: number | null; lng: number | null };

const isPoint = (
  p: Point | null | undefined
): p is { lat: number; lng: number } =>
  typeof p?.lat === 'number' &&
  typeof p?.lng === 'number' &&
  Number.isFinite(p.lat) &&
  Number.isFinite(p.lng) &&
  !(p.lat === 0 && p.lng === 0);

/**
 * 0–1 along the journey by distance, as the map shows it: travelled (from
 * the origin along the recorded path to where it is now) out of travelled
 * plus what is left (straight on to the destination, like "Distance left").
 * Not by time: a shipment long past its ETA but far from its destination
 * must not read as nearly there. Null before pickup or without the places;
 * 1 once delivered.
 */
export const tripProgress = ({
  pickedUpAt,
  deliveredAt,
  origin,
  destination,
  path,
  position,
}: {
  pickedUpAt: string | null;
  deliveredAt: string | null;
  origin: Point | null;
  destination: Point | null;
  path?: Point[] | null;
  position: Point | null;
}): number | null => {
  if (deliveredAt) return 1;
  if (!pickedUpAt) return null;
  if (!isPoint(origin) || !isPoint(destination) || !isPoint(position))
    return null;
  const route = [origin, ...(path ?? []).filter(isPoint), position];
  let travelled = 0;
  for (let i = 1; i < route.length; i += 1)
    travelled += haversineKm(
      route[i - 1].lat,
      route[i - 1].lng,
      route[i].lat,
      route[i].lng
    );
  const left = haversineKm(
    position.lat,
    position.lng,
    destination.lat,
    destination.lng
  );
  if (travelled + left === 0) return null;
  return travelled / (travelled + left);
};

/** "3h 23m" to go, "Overdue by 40m" once the ETA has passed. */
export const formatTimeToDestination = (
  eta: string | null,
  now: number
): string | null => {
  const end = toMs(eta);
  if (end === null) return null;
  const minutes = Math.round((end - now) / 60000);
  return minutes >= 0
    ? formatDurationMinutes(minutes)
    : `Overdue by ${formatDurationMinutes(-minutes)}`;
};

/** US screens read miles; everyone else kilometres. */
export const usesMiles = (locale: string): boolean => /^en-US\b/i.test(locale);

/** "214 mi" on US screens, "344 km" elsewhere. */
export const formatKm = (km: number, locale: string): string | null => {
  if (!Number.isFinite(km)) return null;
  return usesMiles(locale)
    ? `${Math.round(km * 0.621371).toLocaleString()} mi`
    : `${Math.round(km).toLocaleString()} km`;
};

/** Straight-line distance from the last ping to the destination. */
export const formatDistanceLeft = (
  from: { lat: number; lng: number } | null,
  destination: MonitorTripPlace | null,
  locale: string
): string | null => {
  if (
    !from ||
    !destination ||
    typeof destination.lat !== 'number' ||
    typeof destination.lng !== 'number'
  )
    return null;
  const km = haversineKm(from.lat, from.lng, destination.lat, destination.lng);
  return formatKm(km, locale);
};

/** "14:32 EDT" at the destination, or null without a usable timezone. */
export const formatLocalTimeAt = (
  timeZone: string | null | undefined,
  now: number
): string | null => {
  if (!timeZone) return null;
  try {
    return new Date(now).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'short',
      timeZone,
    });
  } catch {
    return null;
  }
};

/** "4 min ago" for the newest reading or ping. */
export const formatAgo = (atMs: number | null, now: number): string | null => {
  if (atMs === null) return null;
  const minutes = Math.max(0, Math.round((now - atMs) / 60000));
  return minutes === 0 ? 'Just now' : `${formatDurationMinutes(minutes)} ago`;
};

const lastTime = (series: MeasureSeries | undefined): number | null =>
  series?.t?.length ? series.t[series.t.length - 1] : null;

export interface TemperatureTrack {
  readings: MeasureSeries;
  /** Newest temperature reading or location ping, epoch ms. */
  lastUpdateAt: number | null;
}

/**
 * The sensor with the most temperature readings (a shipment can carry more
 * than one), plus the newest reading or location ping across all sensors.
 */
export const pickTemperatureTrack = (
  series: SeriesBySensor | null | undefined
): TemperatureTrack | null => {
  const sensors = Object.values(series ?? {});
  const temperature = sensors
    .map((measures) => measures.temperature)
    .filter((s): s is MeasureSeries => Boolean(s?.t?.length))
    .sort((a, b) => b.t.length - a.t.length)[0];
  const times = sensors
    .flatMap((measures) => [
      lastTime(measures.temperature),
      lastTime(measures.latitude),
    ])
    .filter((t): t is number => t !== null);
  if (!temperature && !times.length) return null;
  return {
    readings: temperature ?? { t: [], v: [] },
    lastUpdateAt: times.length ? Math.max(...times) : null,
  };
};

export const isOutsideBand = (
  value: number,
  band: { min: number | null; max: number | null } | null
): boolean =>
  Boolean(
    band &&
    ((band.min !== null && value < band.min) ||
      (band.max !== null && value > band.max))
  );
